import { useTranslation } from '@/i18n'
import { normalizeAgentName, getDisplayName, AgentId, AGENT_ALIASES } from '@/constants/agentNames'
import { useUIv2 } from '@/hooks/useUIv2'
import EmptyChatStateV2 from './EmptyChatStateV2'

interface EmptyChatStateProps {
  activeAgent?: string
}

// Agent avatar configuration using canonical agent IDs
const AGENT_AVATAR_CONFIG: Record<string, { image: string; bgColor: string }> = {
  [AgentId.PLANT_SUPPORT]: { image: '/images/plant-support-agent.png', bgColor: '#FEF08A' },
  [AgentId.CHEMICAL]: { image: '/images/chemical-agent.png', bgColor: '#BFDBFE' },
  [AgentId.MECHANICAL]: { image: '/images/mechanical-agent.png', bgColor: '#BBF7D0' },
  [AgentId.ELECTRICAL]: { image: '/images/elec-agent.png', bgColor: '#E9D5FF' },
}

const DEFAULT_AVATAR = AGENT_AVATAR_CONFIG[AgentId.PLANT_SUPPORT]

const EmptyChatState = ({ activeAgent = AgentId.PLANT_SUPPORT }: EmptyChatStateProps) => {
  const isV2 = useUIv2()
  const { t } = useTranslation()

  if (isV2) {
    return <EmptyChatStateV2 activeAgent={activeAgent} />
  }

  // Get agent avatar using normalized name
  const getAgentAvatar = (agent: string) => {
    const normalizedAgent = normalizeAgentName(agent)
    const config = AGENT_AVATAR_CONFIG[normalizedAgent] || DEFAULT_AVATAR
    return { ...config, name: getDisplayName(normalizedAgent) }
  }

  const agentAvatar = getAgentAvatar(activeAgent)
  const normalizedAgent = normalizeAgentName(activeAgent)
  const legacyKey = (typeof normalizedAgent === 'string' && AGENT_ALIASES[normalizedAgent]?.legacyShort) || 'pam'
  const descriptionTKey =
    legacyKey === 'aice'
      ? 'chat:empty_state.descriptions.aice'
      : legacyKey === 'max'
        ? 'chat:empty_state.descriptions.max'
        : legacyKey === 'ella'
          ? 'chat:empty_state.descriptions.ella'
          : 'chat:empty_state.descriptions.pam'

  return (
    <div className="flex flex-col h-full text-center px-4 py-8 gap-3 justify-center">
      <figure className="flex justify-center">
        <div className="w-16 rounded-full shadow-md flex items-center justify-center overflow-hidden" style={{ backgroundColor: agentAvatar.bgColor }}>
          <img
            src={agentAvatar.image}
            alt={agentAvatar.name}
            className="w-16 object-cover rounded-full"
            draggable={false}
          />
        </div>
      </figure>
      <h3 className="text-gray-600 dark:text-gray-400 font-medium">
        {t('chat:empty_state.title')}
      </h3>
      <p className="text-gray-400 dark:text-gray-500">{t(descriptionTKey)}</p>
    </div>
  )
}

export default EmptyChatState
